import React from 'react';
import { Badge, Box, Flex, IconButton, SimpleGrid, Spinner, Text, Tooltip } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { ArrowClockwise, DownloadSimple, Eye, ListBullets, Trash } from 'phosphor-react';

import { GlassCard } from '../../../../../../components/glass';
import type { IExtratoItem } from '../../../../../../hooks/useContaCilExtratos';
import { STATUS_ACCENT, STATUS_UI, fmtDataHora, fmtNumero, fmtPeriodo, fmtValor } from '../format';
import { VeredictoBadge } from './VeredictoExtrato';

const ACAO_INDISPONIVEL = 'Arquivo indisponível (expirado ou anterior ao recurso)';

/** Quanto tempo o card do deep-link fica realçado antes de voltar ao normal. */
const DESTAQUE_MS = 20_000;

function corConfianca(pct: number | null): string {
  if (pct == null) return '#94A3B8';
  if (pct >= 85) return '#10B981';
  if (pct >= 60) return '#F59E0B';
  return '#EF4444';
}

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box minW={0}>
      <Text
        fontSize="9px"
        fontWeight="600"
        color="#94A3B8"
        textTransform="uppercase"
        letterSpacing="0.06em"
        mb="2px"
      >
        {label}
      </Text>
      <Box fontSize="12px" color="#0F172A" fontWeight="600" isTruncated>
        {children}
      </Box>
    </Box>
  );
}

// Tipos detectados pelo ETL que NAO sao extrato bancario — exibir badge
// destacando pro usuario que o PDF subiu errado e nao entrou no fluxo de
// conciliacao normal. EXTRATO_CC/EXTRATO_SEM_MOVIMENTO/DESCONHECIDO/null
// nao mostram nada (sao o caso default).
const TIPO_DOCUMENTO_LABEL: Record<string, { label: string; colorScheme: string }> = {
  nota_fiscal: { label: 'Nota fiscal — não é extrato', colorScheme: 'orange' },
  comprovante_avulso: { label: 'Comprovante — não é extrato', colorScheme: 'orange' },
  tela_sistema: { label: 'Print de sistema', colorScheme: 'orange' },
  relatorio_erp: { label: 'Relatório de ERP', colorScheme: 'orange' },
  informe_rendimento: { label: 'Informe de rendimentos', colorScheme: 'orange' },
  extrato_investimento: { label: 'Extrato de investimento', colorScheme: 'purple' },
  fatura_cartao: { label: 'Fatura de cartão', colorScheme: 'pink' },
  resumo_mensal: { label: 'Resumo mensal', colorScheme: 'blue' },
  // O ETL não conseguiu decidir o tipo — retém sem afirmar. Substitui o
  // `fatura_cartao` que era devolvido no caso ambíguo e errava em 13 de 13
  // (todos extratos de conta corrente com linha de gasto de cartão). Sem esta
  // entrada o badge sumiria e o card ficaria retido sem dizer por quê.
  indeterminado: { label: 'Tipo não identificado', colorScheme: 'yellow' },
};

function TipoDocumentoBadge({ tipo }: { tipo?: string | null }) {
  if (!tipo) return null;
  const meta = TIPO_DOCUMENTO_LABEL[tipo];
  if (!meta) return null;
  return (
    <Badge
      colorScheme={meta.colorScheme}
      borderRadius="md"
      fontSize="9px"
      px={1.5}
      textTransform="none"
      flexShrink={0}
    >
      {meta.label}
    </Badge>
  );
}

// Natureza da conta detectada pelo ETL. So destacamos o que foge do comum:
// corrente nao mostra nada (maioria) e investimento ja e' coberto pelo badge de
// tipo_documento ("Extrato de investimento") — andam sempre juntos.
const TIPO_CONTA_LABEL: Record<string, { label: string; colorScheme: string }> = {
  poupanca: { label: 'Poupança', colorScheme: 'green' },
  salario: { label: 'Conta-salário', colorScheme: 'cyan' },
};

function TipoContaBadge({ tipo }: { tipo?: string | null }) {
  if (!tipo) return null;
  const meta = TIPO_CONTA_LABEL[tipo];
  if (!meta) return null;
  return (
    <Badge
      colorScheme={meta.colorScheme}
      borderRadius="md"
      fontSize="9px"
      px={1.5}
      textTransform="none"
      flexShrink={0}
    >
      {meta.label}
    </Badge>
  );
}

/**
 * Chip âmbar PERMANENTE da divergência não discriminada pelo banco (drift de
 * saldo sem lançamento impresso — rendimento/taxa de conta remunerada).
 * Com pendências, indica que a conciliação está bloqueada até o contador
 * resolver o quiz no detalhe; resolvidas, permanece como informação.
 */
function DivergenciaBancoPill({
  categoria,
  pendentes,
  valorDiv,
}: {
  categoria?: string | null;
  pendentes?: number;
  valorDiv?: number | null;
}) {
  if (!categoria) return null;
  const temPendente = (pendentes ?? 0) > 0;
  return (
    <Box
      as="span"
      px={2}
      py={0.5}
      borderRadius="full"
      fontSize="11px"
      fontWeight="600"
      bg="rgba(245, 158, 11, 0.16)"
      color="#B45309"
      cursor="help"
      title={
        temPendente
          ? `Divergência de ${fmtValor(valorDiv)} não discriminada pelo banco — ` +
            `${pendentes} janela(s) pendente(s); conciliação bloqueada até resolver no detalhe`
          : `Divergência de ${fmtValor(valorDiv)} não discriminada pelo banco — resolvida (informativo)`
      }
    >
      não discriminado
      {temPendente ? ` · ${pendentes} pendente${(pendentes ?? 0) > 1 ? 's' : ''}` : ''}
    </Box>
  );
}

// Motivo de o fecho não ter sido verificado contra saldo do banco.
function ancoraHint(ancora?: string | null): string {
  if (ancora === 'si_apenas') {
    return (
      'Saldo inicial confere, mas o extrato não traz saldo final do banco ' +
      'pra confirmar os lançamentos — revise antes de conciliar'
    );
  }
  return (
    'O extrato não traz saldo do banco pra confirmar o fecho (calculado a ' +
    'partir dos lançamentos) — revise antes de conciliar'
  );
}

/**
 * Contagem de lançamentos do extrato.
 *
 * Só alerta no caso que motivou a mudança: extrato CONCLUÍDO que reportou
 * transações e está com ZERO lançamentos vivos — a "garantia falsa" que
 * escondeu o sumiço dos lançamentos da Associação em 30/07 ("6 txns ·
 * CONFIÁVEL" com nada no banco).
 *
 * Deliberadamente NÃO alerta em qualquer divergência entre `qtd_transacoes` e
 * `vinculados`. Medido na base: a regra ampla acenderia em 47 de 402 extratos e
 * ~43 seriam falso alarme, afogando o sinal real (6 casos). Duas causas
 * legítimas de divergência:
 *   - 98,8% dos lançamentos ainda têm `import_id` nulo, então a contagem cai no
 *     ramo por nome de arquivo e SOMA o pool inteiro de extratos homônimos
 *     (empresa 1044 exibiria "659 de 324");
 *   - dedup: `qtd_transacoes − vinculados == qtd_duplicadas` é o esperado.
 * Quando o backfill de `import_id` rodar, vale reavaliar alargar a regra.
 */
function TxnsPill({
  noExtrato,
  vinculados,
  status,
}: {
  noExtrato?: number | null;
  vinculados?: number | null;
  status?: string;
}) {
  // Só extrato concluído: nos demais status (retido, processando, erro) zero
  // lançamento é o estado correto, não anomalia.
  const semVinculo =
    status === 'concluido' && vinculados === 0 && typeof noExtrato === 'number' && noExtrato > 0;

  if (!semVinculo) {
    return (
      <Flex align="center" gap={1}>
        <ListBullets size={13} color="#94A3B8" />
        <Text fontSize="11px" color="#475569" fontWeight="600">
          {fmtNumero(noExtrato)} txns
        </Text>
      </Flex>
    );
  }
  return (
    <Flex
      align="center"
      gap={1}
      cursor="help"
      title={
        `O extrato tem ${fmtNumero(noExtrato)} transações, mas nenhuma está ` +
        'vinculada. Os lançamentos foram excluídos ou o reprocessamento não ' +
        'concluiu — não há o que conciliar.'
      }
    >
      <ListBullets size={13} color="#B45309" />
      <Text fontSize="11px" color="#B45309" fontWeight="700">
        0 de {fmtNumero(noExtrato)} txns
      </Text>
    </Flex>
  );
}

function BalancoPill({
  ok,
  divergencia,
  verificado,
  ancora,
}: {
  ok?: boolean | null;
  divergencia?: number | null;
  verificado?: boolean | null;
  ancora?: string | null;
}) {
  if (ok == null) return null;
  if (ok) {
    // Balanço fechou, mas por derivação (SF=SI+net) sem saldo independente do
    // banco pra confirmar — não é "confere" cego, é "revisar".
    if (verificado === false) {
      return (
        <Box
          as="span"
          px={2}
          py={0.5}
          borderRadius="full"
          fontSize="11px"
          fontWeight="600"
          bg="rgba(245, 158, 11, 0.16)"
          color="#B45309"
          cursor="help"
          title={ancoraHint(ancora)}
        >
          fecho não-verificado
        </Box>
      );
    }
    return (
      <Box
        as="span"
        px={2}
        py={0.5}
        borderRadius="full"
        fontSize="11px"
        fontWeight="600"
        bg="rgba(16, 185, 129, 0.1)"
        color="#059669"
      >
        balanço confere
      </Box>
    );
  }
  return (
    <Box
      as="span"
      px={2}
      py={0.5}
      borderRadius="full"
      fontSize="11px"
      fontWeight="600"
      bg="rgba(239, 68, 68, 0.1)"
      color="#DC2626"
      title={`Divergência ${fmtValor(divergencia)}`}
    >
      diverge
    </Box>
  );
}

interface Props {
  extrato: IExtratoItem;
  index: number;
  onOpen: (item: IExtratoItem) => void;
  onBaixar: (item: IExtratoItem) => void;
  onReprocessar: (item: IExtratoItem) => void;
  onExcluir: (item: IExtratoItem) => void;
  baixando: boolean;
  reprocessando: boolean;
  /**
   * Exclusão em curso. Fica ativo até a LISTA confirmar (o pai só limpa depois
   * do refetch), não até o DELETE responder: quem reimportava nesse intervalo
   * esbarrava no "já importado" e concluía que a exclusão tinha falhado.
   */
  excluindo?: boolean;
  /**
   * Card alvo de um deep-link (`?extrato=`). Ganha anel e rola até a vista —
   * sem isso o operador cai numa lista filtrada sem saber QUAL dos cards é o
   * dele, que é o ponto do atalho.
   */
  destacado?: boolean;
}

export function ExtratoCard({
  extrato: e,
  index,
  onOpen,
  onBaixar,
  onReprocessar,
  onExcluir,
  baixando,
  reprocessando,
  excluindo = false,
  destacado = false,
}: Props) {
  const st = STATUS_UI[e.status];
  const accent = STATUS_ACCENT[e.status];
  const titulo = e.empresa_nome || e.titular_detectado || e.cnpj_detectado || '—';
  const semEmpresa = !e.empresa_nome;
  const pctConf = e.confianca_global != null ? Math.round(e.confianca_global * 100) : null;
  const ref = React.useRef<HTMLDivElement>(null);
  // O realce é temporário: serve pra achar o card, não pra marcá-lo. Passado o
  // tempo de localizar, o anel vira ruído — e pior, sugere um estado do
  // documento que não existe. O `destacado` continua valendo (é o alvo do
  // deep-link); só a pintura sai.
  const [realcado, setRealcado] = React.useState(destacado);

  // Rola até o card do deep-link. `block: 'center'` porque a lista é uma grade
  // de 3 colunas: alinhar no topo esconderia os vizinhos e tiraria justamente a
  // noção de "onde ele está" que o atalho existe pra dar.
  React.useEffect(() => {
    if (!destacado || !ref.current) return;
    ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [destacado]);

  React.useEffect(() => {
    if (!destacado) {
      setRealcado(false);
      return undefined;
    }
    setRealcado(true);
    const t = setTimeout(() => setRealcado(false), DESTAQUE_MS);
    return () => clearTimeout(t);
  }, [destacado]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      // Teto no stagger: `0.04 * index` sem limite deixava o 40o card 1,6s
      // parado em opacity 0 — indistinguivel de card quebrado pra quem rolou
      // ate ele. 9 e o suficiente pra escada aparecer nas 3 primeiras linhas.
      transition={{
        duration: 0.4,
        delay: 0.04 * Math.min(index, 9),
        ease: [0.4, 0, 0.2, 1],
      }}
    >
      <GlassCard
        intensity="medium"
        hoverable={!excluindo}
        tint={accent}
        p={0}
        position="relative"
        // Usa a prop `destacado` do GlassCard em vez de sobrescrever borda e
        // sombra: passar `undefined` nessas props APAGA o estilo base, porque o
        // GlassCard espalha `{...rest}` depois dos próprios valores.
        destacado={realcado}
        cursor={excluindo ? 'progress' : 'pointer'}
        role="button"
        tabIndex={excluindo ? -1 : 0}
        aria-busy={excluindo}
        aria-label={`Extrato ${e.nome_arquivo}, ${st?.label ?? e.status}`}
        onClick={() => {
          if (!excluindo) onOpen(e);
        }}
        onKeyDown={ev => {
          if (!excluindo && (ev.key === 'Enter' || ev.key === ' ')) {
            ev.preventDefault();
            onOpen(e);
          }
        }}
      >
        {/* Cobre o card inteiro enquanto exclui: bloqueia clique e deixa
            explicito que a operacao ainda esta em curso. */}
        {excluindo && (
          <Flex
            position="absolute"
            inset={0}
            zIndex={2}
            align="center"
            justify="center"
            gap={2}
            borderRadius="10px"
            bg="rgba(255, 255, 255, 0.82)"
            backdropFilter="blur(1px)"
          >
            <Spinner size="sm" color="#DC2626" thickness="2px" />
            <Text fontSize="12px" fontWeight="700" color="#B91C1C">
              Excluindo extrato…
            </Text>
          </Flex>
        )}
        <Flex>
          <Box w="4px" bg={accent} flexShrink={0} alignSelf="stretch" />
          <Flex direction="column" gap={3} p={4} flex={1} minW={0}>
            {/* Cabeçalho */}
            <Flex align="flex-start" justify="space-between" gap={2}>
              <Box minW={0}>
                <Text
                  fontSize="sm"
                  fontWeight="700"
                  color={semEmpresa ? '#94A3B8' : '#0F172A'}
                  isTruncated
                  title={titulo}
                >
                  {titulo}
                </Text>
                <Flex align="center" gap={1.5} mt={0.5}>
                  <Text
                    fontSize="11px"
                    color="#64748B"
                    isTruncated
                    maxW="160px"
                    title={e.nome_arquivo}
                  >
                    {e.nome_arquivo}
                  </Text>
                  <Badge
                    colorScheme="gray"
                    borderRadius="md"
                    fontSize="9px"
                    px={1.5}
                    flexShrink={0}
                  >
                    {e.formato}
                  </Badge>
                  <TipoDocumentoBadge tipo={e.tipo_documento} />
                  <TipoContaBadge tipo={e.tipo_conta} />
                </Flex>
              </Box>
              <Badge colorScheme={st?.cor ?? 'gray'} borderRadius="full" px={2} flexShrink={0}>
                {st?.label ?? e.status}
              </Badge>
            </Flex>

            <Box h="1px" bg="rgba(15, 23, 42, 0.06)" />

            {/* Banco/conta, período e saldos */}
            <SimpleGrid columns={2} spacingX={3} spacingY={2}>
              <Campo label="Banco / Conta">
                {e.banco_nome ?? '—'}
                {e.conta && (
                  <Text as="span" fontSize="10px" color="#94A3B8" fontWeight="500">
                    {' '}
                    · {e.conta}
                  </Text>
                )}
              </Campo>
              <Campo label="Período">
                <Text fontSize="12px" color="#475569" fontWeight="600">
                  {fmtPeriodo(e.periodo_ini, e.periodo_fim)}
                </Text>
              </Campo>
              <Campo label="Saldo inicial">{fmtValor(e.saldo_inicial)}</Campo>
              <Campo label="Saldo final">{fmtValor(e.saldo_final)}</Campo>
            </SimpleGrid>

            {/* Métricas */}
            <Flex align="center" gap={3} wrap="wrap">
              <TxnsPill
                noExtrato={e.qtd_transacoes}
                vinculados={e.qtd_lancamentos}
                status={e.status}
              />
              <BalancoPill
                ok={e.balanco_ok}
                divergencia={e.divergencia_balanco}
                verificado={e.balanco_verificado}
                ancora={e.balanco_ancora}
              />
              <DivergenciaBancoPill
                categoria={e.divergencia_categoria}
                pendentes={e.divergencias_pendentes}
                valorDiv={e.divergencia_balanco}
              />
              {pctConf != null && (
                <Flex align="center" gap={1}>
                  <Box w="6px" h="6px" borderRadius="full" bg={corConfianca(pctConf)} />
                  <Text fontSize="11px" color="#64748B" fontWeight="600">
                    {pctConf}%
                  </Text>
                </Flex>
              )}
              <VeredictoBadge
                veredito={e.critica_saldo}
                retido={e.status === 'aguardando_validacao'}
              />
            </Flex>

            <Box h="1px" bg="rgba(15, 23, 42, 0.06)" />

            {/* Rodapé: enviado por + ações */}
            <Flex align="center" justify="space-between" gap={2}>
              <Box minW={0}>
                <Text
                  fontSize="11px"
                  color="#475569"
                  isTruncated
                  title={
                    e.importado_por_nome
                      ? `${e.importado_por_nome} · ${e.importado_por_email ?? ''}`
                      : (e.importado_por_email ?? '')
                  }
                >
                  {/* Nome e e-mail juntos: o e-mail sozinho não identifica a
                      pessoa, e o nome sozinho perde a rastreabilidade (o e-mail
                      é o snapshot gravado no import). */}
                  {e.importado_por_nome
                    ? `${e.importado_por_nome} · ${e.importado_por_email ?? ''}`
                    : (e.importado_por_email ?? '—')}
                </Text>
                <Text fontSize="10px" color="#94A3B8">
                  {fmtDataHora(e.dt_criacao)}
                  {e.importado_por_origem ? ` · ${e.importado_por_origem}` : ''}
                </Text>
              </Box>
              <Flex gap={0.5} flexShrink={0} onClick={ev => ev.stopPropagation()}>
                <Tooltip label="Ver detalhe" hasArrow shouldWrapChildren>
                  <IconButton
                    aria-label="Ver detalhe"
                    icon={<Eye size={15} weight="bold" />}
                    size="xs"
                    variant="ghost"
                    borderRadius="full"
                    color="#64748B"
                    _hover={{ bg: 'rgba(27, 136, 245, 0.1)', color: '#1B88F5' }}
                    onClick={() => onOpen(e)}
                  />
                </Tooltip>
                <Tooltip
                  label={e.pode_baixar ? 'Baixar arquivo original' : ACAO_INDISPONIVEL}
                  hasArrow
                  shouldWrapChildren
                >
                  <IconButton
                    aria-label="Baixar arquivo"
                    icon={<DownloadSimple size={15} weight="bold" />}
                    size="xs"
                    variant="ghost"
                    borderRadius="full"
                    color="#64748B"
                    isDisabled={!e.pode_baixar || baixando}
                    isLoading={baixando}
                    _hover={{ bg: 'rgba(27, 136, 245, 0.1)', color: '#1B88F5' }}
                    onClick={() => onBaixar(e)}
                  />
                </Tooltip>
                <Tooltip
                  label={e.pode_reprocessar ? 'Ler o documento de novo' : ACAO_INDISPONIVEL}
                  hasArrow
                  shouldWrapChildren
                >
                  <IconButton
                    aria-label="Reprocessar"
                    icon={<ArrowClockwise size={15} weight="bold" />}
                    size="xs"
                    variant="ghost"
                    borderRadius="full"
                    color="#64748B"
                    isDisabled={!e.pode_reprocessar || reprocessando}
                    isLoading={reprocessando}
                    _hover={{ bg: 'rgba(99, 102, 241, 0.1)', color: '#6366F1' }}
                    onClick={() => onReprocessar(e)}
                  />
                </Tooltip>
                <Tooltip label="Excluir" hasArrow shouldWrapChildren>
                  <IconButton
                    aria-label="Excluir"
                    icon={<Trash size={15} weight="bold" />}
                    size="xs"
                    variant="ghost"
                    borderRadius="full"
                    color="#64748B"
                    _hover={{ bg: 'rgba(239, 68, 68, 0.1)', color: '#DC2626' }}
                    onClick={() => onExcluir(e)}
                  />
                </Tooltip>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </GlassCard>
    </motion.div>
  );
}
